// Errori dell'accesso con Google o Microsoft. Quando l'accesso non va a buon
// fine, sync-channel riporta il cliente al pannello con
// `?auth=error&reason=<codice>` (src/auth/gmail/gmail-auth-handler.ts,
// src/auth/outlook/outlook-auth-handler.ts, src/index.ts). Qui ogni codice
// diventa una spiegazione in parole semplici; un codice nuovo che il pannello
// non conosce ancora riceve il messaggio generico, mai il silenzio.
const KEY_PREFIX = 'INBOX_MGMT.OAUTH_ERROR';

export const OAUTH_ERROR_REASONS = Object.freeze({
  // Il cliente ha premuto "Annulla" o non ha dato il permesso.
  authorization_denied: 'CONSENT_DENIED',
  // Microsoft aziendale: serve l'ok di chi gestisce gli account dell'hotel.
  admin_consent_required: 'ADMIN_CONSENT_REQUIRED',
  // Accesso fatto con una casella diversa da quella scritta.
  mailbox_identity_unverified: 'MAILBOX_MISMATCH',
  mailbox_mismatch: 'MAILBOX_MISMATCH',
  // Collegamento scaduto, riaperto da un'altra finestra o gia' usato.
  missing_state: 'EXPIRED',
  invalid_state: 'EXPIRED',
  missing_code: 'EXPIRED',
  // L'utente non e' piu' nell'account (o l'account non e' attivo).
  authorization_revoked: 'ACCESS_REVOKED',
  // La casella e' gia' collegata in un altro modo o su un altro account.
  provider_conflict: 'PROVIDER_CONFLICT',
  // La casella da ricollegare non c'e' piu' o e' cambiata nel frattempo.
  channel_not_found: 'CHANNEL_CHANGED',
  channel_ambiguous: 'CHANNEL_CHANGED',
  channel_binding_changed: 'CHANNEL_CHANGED',
  // Google o Microsoft non hanno risposto come dovevano.
  token_exchange_failed: 'PROVIDER_FAILED',
  user_info_failed: 'PROVIDER_FAILED',
  oauth_failed: 'PROVIDER_FAILED',
});

// Login Instagram: il ritorno (InstagramOauthCallbacksController) aggiunge
// `provider=instagram`, cosi' i testi parlano di Instagram e non di Google o
// Microsoft. Stessa regola: un codice sconosciuto riceve il messaggio generico.
export const INSTAGRAM_OAUTH_ERROR_REASONS = Object.freeze({
  authorization_denied: 'CONSENT_DENIED',
  invalid_state: 'EXPIRED',
  missing_code: 'EXPIRED',
  not_authorized: 'ACCESS_REVOKED',
  provider_conflict: 'PROVIDER_CONFLICT',
  channel_mismatch: 'CHANNEL_MISMATCH',
  inbox_limit_reached: 'INBOX_LIMIT',
  subscription_failed: 'SUBSCRIPTION_FAILED',
  token_exchange_failed: 'PROVIDER_FAILED',
  user_info_failed: 'PROVIDER_FAILED',
  oauth_failed: 'PROVIDER_FAILED',
});
const INSTAGRAM_PROVIDER = 'instagram';

// Il modulo della casella mostra gia' da se' questi due motivi, accanto al
// campo dell'indirizzo (Email.vue): li' l'avviso generale sarebbe un doppione.
const REASONS_SHOWN_BY_EMAIL_SETUP = [
  'mailbox_mismatch',
  'mailbox_identity_unverified',
];
const EMAIL_SETUP_ROUTE = 'settings_inboxes_page_channel';

const firstValue = (value) => (Array.isArray(value) ? value[0] : value);

export function oauthErrorFromQuery(query) {
  if (firstValue(query?.auth) !== 'error') return null;

  const rawReason = firstValue(query?.reason);
  const reason = typeof rawReason === 'string' ? rawReason : '';
  const instagram = firstValue(query?.provider) === INSTAGRAM_PROVIDER;
  const reasons = instagram
    ? INSTAGRAM_OAUTH_ERROR_REASONS
    : OAUTH_ERROR_REASONS;
  const prefix = instagram ? `${KEY_PREFIX}.INSTAGRAM` : KEY_PREFIX;
  const kind = Object.prototype.hasOwnProperty.call(reasons, reason)
    ? reasons[reason]
    : 'PROVIDER_FAILED';

  return {
    reason,
    kind,
    provider: instagram ? INSTAGRAM_PROVIDER : null,
    titleKey: `${prefix}.TITLE`,
    messageKey: `${prefix}.${kind}`,
    hintKey:
      kind === 'ADMIN_CONSENT_REQUIRED'
        ? `${KEY_PREFIX}.ADMIN_CONSENT_IT_HINT`
        : null,
  };
}

// Solo `auth` e `reason`: servono al router per non perderli quando porta
// `/?auth=error...` alla pagina iniziale dell'account.
export function oauthErrorQuery(query) {
  if (firstValue(query?.auth) !== 'error') return null;

  const reason = firstValue(query?.reason);
  const result =
    typeof reason === 'string' && reason
      ? { auth: 'error', reason }
      : { auth: 'error' };
  if (firstValue(query?.provider) === INSTAGRAM_PROVIDER) {
    result.provider = INSTAGRAM_PROVIDER;
  }
  return result;
}

export function withoutOAuthErrorQuery(query = {}) {
  const { auth, reason, provider, ...rest } = query || {};
  return rest;
}

export function isOAuthErrorShownByPage(route, error) {
  return (
    route?.name === EMAIL_SETUP_ROUTE &&
    REASONS_SHOWN_BY_EMAIL_SETUP.includes(error?.reason)
  );
}
